import { useCallback, useEffect, useRef } from "react";
import { useNavigationType, useSearchParams } from "react-router-dom";

import { DEFAULT_SLOT } from "../api/agentSlot";

export const SLOT_PARAM = "config";

/** The slot a project's pages are viewing, held in `?config=`.
 *
 * A link inside the project that drops the parameter (most do: they predate slots) keeps
 * the slot in view rather than silently landing on default; going back, or arriving from
 * outside the project, reads the URL as it is. */
export const useProjectSlot = (projectId: string) => {
  const [params, setParams] = useSearchParams();
  const navigationType = useNavigationType();
  const inView = useRef(new Map<string, string>());
  const fromUrl = params.get(SLOT_PARAM);
  const carried = navigationType === "POP" ? undefined : inView.current.get(projectId);
  const slot = fromUrl ?? carried ?? DEFAULT_SLOT;

  useEffect(() => {
    inView.current.set(projectId, slot);
    if (fromUrl !== null || slot === DEFAULT_SLOT) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(SLOT_PARAM, slot);
        return next;
      },
      { replace: true },
    );
  }, [projectId, slot, fromUrl, setParams]);

  const choose = useCallback(
    (next: string, { replace = false }: { replace?: boolean } = {}) => {
      inView.current.set(projectId, next);
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          if (next === DEFAULT_SLOT) updated.delete(SLOT_PARAM);
          else updated.set(SLOT_PARAM, next);
          return updated;
        },
        { replace },
      );
    },
    [projectId, setParams],
  );

  return { slot, choose };
};
